/**
 * MainNavigation Component
 *
 * Main navigation shell that combines TopNav, SubNav, and ViewRenderer.
 * This is the entry point for the new navigation system.
 *
 * Navigation Contract:
 * - Views are pure renderers; they receive an onNavigateLegacy prop for internal navigation
 * - Views must NOT import navigationUtils directly
 * - All URL changes go through useNavigation hook
 *
 * @see docs/architecture/2026-01-08/NAVIGATION-RESTRUCTURE-MASTER-PLAN.md
 */

import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigation } from '../../hooks/useNavigation';
import { resolveLegacyAction, emitNavEvent } from '../../utils/navigationUtils';
import { getViewConfig } from '../../config/viewRegistry';
import TopNav from './TopNav';
import SubNav from './SubNav';
import ViewRenderer from './ViewRenderer';

/**
 * MainNavigation is the root navigation component
 * @param {object} props
 * @param {object} props.user - User object with isAdmin and isPM flags
 * @param {function} props.onSignOut - Handler for sign out action
 * @param {function} props.onSwitchAccount - Handler for switch account action
 * @param {function} props.onShowProfile - Handler for profile action
 * @param {function} props.onShowDownloads - Handler for downloads action
 * @param {function} props.onShowFeedback - Handler for feedback action
 * @param {object} props.viewProps - Additional props to pass to all views
 */
function MainNavigation({
  user,
  onSignOut,
  onSwitchAccount,
  onShowProfile,
  onShowDownloads,
  onShowFeedback,
  onReprintRemake,
  viewProps = {},
}) {
  const {
    activeSection,
    activeView,
    activeParam,
    isNavReady,
    navigateTo,
  } = useNavigation(user);

  // Handle section clicks (from TopNav)
  const handleSectionClick = useCallback(
    (sectionId) => {
      navigateTo(sectionId);
      emitNavEvent('section_click', { section: sectionId }, user);
    },
    [navigateTo, user]
  );

  // Handle view clicks (from SubNav)
  const handleViewClick = useCallback(
    (sectionId, viewId) => {
      navigateTo(sectionId, viewId);
      emitNavEvent('view_click', { section: sectionId, view: viewId }, user);
    },
    [navigateTo, user]
  );

  // Handle account menu actions
  const handleAccountAction = useCallback(
    (action, viewId) => {
      emitNavEvent('account_action', { action, view_id: viewId }, user);

      switch (action) {
        case 'signOut':
          onSignOut?.();
          break;
        case 'switchAccount':
          onSwitchAccount?.();
          break;
        case 'navigate':
          // Check if view has an action instead of component
          const config = getViewConfig(viewId);
          if (config?.action) {
            switch (config.action) {
              case 'showProfile':
                onShowProfile?.();
                break;
              case 'showDownloads':
                onShowDownloads?.();
                break;
              case 'showFeedback':
                onShowFeedback?.();
                break;
              default:
                console.warn('[MainNavigation] Unknown action:', config.action);
            }
          }
          break;
        default:
          console.warn('[MainNavigation] Unknown account action:', action);
      }
    },
    [user, onSignOut, onSwitchAccount, onShowProfile, onShowDownloads, onShowFeedback]
  );

  // Legacy navigation handler for views that use old showXXX pattern
  const handleLegacyNavigate = useCallback(
    (action) => {
      const resolved = resolveLegacyAction(action);
      if (resolved) {
        const [section, view] = resolved;
        navigateTo(section, view);
        emitNavEvent('legacy_navigate', {
          action,
          section,
          view,
        }, user);
      } else {
        console.warn('[MainNavigation] Unknown legacy action:', action);
      }
    },
    [navigateTo, user]
  );

  // Loading state
  if (!isNavReady) {
    return (
      <div className="min-h-screen bg-slate-200 flex items-center justify-center">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-200 flex flex-col">
      {/* Top Navigation */}
      <TopNav
        activeSection={activeSection}
        user={user}
        onSectionClick={handleSectionClick}
        onAccountAction={handleAccountAction}
        onReprintRemake={onReprintRemake}
      />

      {/* Sub Navigation */}
      <SubNav
        sectionId={activeSection}
        activeView={activeView}
        user={user}
        onViewClick={handleViewClick}
      />

      {/* Main Content */}
      <main className="flex-1">
        <ViewRenderer
          viewId={activeView}
          user={user}
          onNavigateLegacy={handleLegacyNavigate}
          viewProps={{
            ...viewProps,
            // Pass URL param (e.g., quoteNumber for project-page)
            routeParam: activeParam,
            // Also provide navigateTo for views that need to navigate with params
            navigateTo,
          }}
        />
      </main>
    </div>
  );
}

MainNavigation.propTypes = {
  user: PropTypes.object,
  onSignOut: PropTypes.func,
  onSwitchAccount: PropTypes.func,
  onShowProfile: PropTypes.func,
  onShowDownloads: PropTypes.func,
  onShowFeedback: PropTypes.func,
  viewProps: PropTypes.object,
};

MainNavigation.defaultProps = {
  user: null,
  onSignOut: null,
  onSwitchAccount: null,
  onShowProfile: null,
  onShowDownloads: null,
  onShowFeedback: null,
  viewProps: {},
};

export default MainNavigation;
